import { useTranslation } from 'react-i18next';

import type { MetricsFilters } from '@/common/api/models/metricsFilters';

import { DateRangeFilter } from './DateRangeFilter';
import { SegmentedFilter } from './SegmentedFilter';
import { SwitchFilter } from './SwitchFilter';
import type { MetricsReportFilterKey } from './reportFilterDefaults';

export type ReportFilterControlProps = {
  filters: MetricsFilters;
  setFilter: <K extends MetricsReportFilterKey>(
    name: K,
    value: MetricsFilters[K extends keyof MetricsFilters ? K : never],
  ) => void;
  disabled?: boolean;
};

export type ReportFilterControlEntry = {
  name: MetricsReportFilterKey;
  render: (props: ReportFilterControlProps) => React.ReactNode;
};

function ExcludePasteHeavyControl(props: ReportFilterControlProps) {
  const { t } = useTranslation();
  const { filters, setFilter, disabled } = props;
  return (
    <SwitchFilter
      testID="report-filter-excludePasteHeavy"
      label={t('admin.reports.filters.excludePasteHeavy')}
      value={filters.excludePasteHeavy}
      onChange={(value) => setFilter('excludePasteHeavy', value)}
      disabled={disabled}
    />
  );
}

function ExcludeFirstTryPassesControl(props: ReportFilterControlProps) {
  const { t } = useTranslation();
  const { filters, setFilter, disabled } = props;
  return (
    <SwitchFilter
      testID="report-filter-excludeFirstTryPasses"
      label={t('admin.reports.filters.excludeFirstTryPasses')}
      value={filters.excludeFirstTryPasses}
      onChange={(value) => setFilter('excludeFirstTryPasses', value)}
      disabled={disabled}
    />
  );
}

/** Registry of filter controls for the metrics report filter bar. */
export const REPORT_FILTER_CONTROLS: Partial<Record<MetricsReportFilterKey, ReportFilterControlEntry['render']>> = {
  gated: ({ filters, setFilter, disabled }) => (
    <SegmentedFilter
      value={filters.gated}
      onChange={(value) => setFilter('gated', value)}
      disabled={disabled}
    />
  ),
  excludePasteHeavy: ExcludePasteHeavyControl,
  excludeFirstTryPasses: ExcludeFirstTryPassesControl,
};

/** Combined date range control (from + to). */
export function DateRangeFilterControl({
  filters,
  setFilter,
  disabled,
  useLast90DaysPreset,
}: ReportFilterControlProps & { useLast90DaysPreset?: boolean }) {
  return (
    <DateRangeFilter
      from={filters.from}
      to={filters.to}
      useLast90DaysPreset={useLast90DaysPreset}
      disabled={disabled}
      onFromChange={(iso) => setFilter('from', iso)}
      onToChange={(iso) => setFilter('to', iso)}
    />
  );
}
